import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createInvoiceLink, createOrder, getConfig, getProduct, getReceipt, similarProducts, trackView, updateProduct } from "../api/endpoints";
import ProductPhoto, { prefetchPhoto, usePhotoSrc } from "../components/ProductPhoto";
import { HandArrow, HandSeal, HandUnderline } from "../components/Hand";
import ProductScroller from "../components/ProductScroller";
import Sheet from "../components/Sheet";
import { PaymentSheet } from "../components/Payment";
import { ReceiptSheet, ReservationVoucher } from "../components/Voucher";
import { ProductSkeleton } from "../components/Skeletons";
import { addRecentlyViewed, shouldCountView } from "../lib/recentlyViewed";
import { askAboutProduct } from "../lib/contact";
import { isTelegram } from "../lib/platform";
import { haptic } from "../lib/tg";
import { useMainButton } from "../hooks/useTelegramUI";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { notify, notifyError } from "../lib/ui";
import { useLikesStore } from "../store/likes";
import { useUserStore } from "../store/user";
import type { Condition, Order, Product, ProductStatus, Receipt, ReservationType } from "../types";
import { CATEGORY_LABELS, CONDITION_LABELS, categoryLabel } from "../types";

const SIZES = [
  "XS", "S", "M", "L", "XL", "XXL", "XXXL",
  "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47",
  "One size",
];

const CONDITIONS: { value: Condition; label: string }[] = [
  { value: "excellent", label: "Отличное" },
  { value: "good",      label: "Хорошее"  },
  { value: "fair",      label: "Удовлетв." },
];

const STATUSES: { value: ProductStatus; label: string }[] = [
  { value: "available", label: "В наличии" },
  { value: "on_hold",   label: "В броне"   },
  { value: "sold",      label: "Продан"    },
];

function EditSheet({
  product,
  onSave,
  onClose,
}: {
  product: Product;
  onSave: (updated: Product) => void;
  onClose: () => void;
}) {
  const [title, setTitle]       = useState(product.title ?? "");
  // Категория: пресет из словаря или «Другое» (ручной ввод). Если у товара уже
  // произвольная категория — открываем сразу в режиме ручного ввода.
  const knownCat = product.category in CATEGORY_LABELS;
  const [category, setCategory] = useState<string>(knownCat ? product.category : "__custom__");
  const [customCat, setCustomCat] = useState(knownCat ? "" : product.category);
  const [size, setSize]         = useState(product.size);
  const [price, setPrice]       = useState(String(Math.round(product.price)));
  const [condition, setCondition] = useState<Condition>(product.condition);
  const [pstatus, setPstatus]   = useState<ProductStatus>(product.status);
  const [busy, setBusy]         = useState(false);

  const categoryValue = category === "__custom__" ? customCat.trim() : category;

  const save = async () => {
    setBusy(true);
    try {
      const updated = await updateProduct(product.id, {
        title: title || undefined,
        category: categoryValue,
        size,
        price: Number(price),
        condition,
        status: pstatus,
      });
      onSave(updated);
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          background: "var(--bg)",
          borderRadius: "20px 20px 0 0",
          padding: "20px 16px 32px",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>Редактировать товар</p>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--hint)" }}>×</button>
        </div>

        <div className="field">
          <label>Название</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например: Куртка Levi's" />
        </div>

        <div className="field">
          <label>Категория</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
            <option value="__custom__">✏️ Другое…</option>
          </select>
          {category === "__custom__" && (
            <input
              value={customCat}
              onChange={(e) => setCustomCat(e.target.value)}
              placeholder="Например: Ремни"
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        <div className="row">
          <div className="field">
            <label>Размер</label>
            {/* Комбо: пресет или ручной ввод (напр. длина ремня «110 см»). */}
            <input
              list="size-options-edit"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="S, M, L… или вручную"
            />
            <datalist id="size-options-edit">
              {SIZES.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div className="field">
            <label>Цена ₽</label>
            <input
              type="number"
              inputMode="numeric"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
        </div>

        <div className="row">
          <div className="field">
            <label>Состояние</label>
            <select value={condition} onChange={(e) => setCondition(e.target.value as Condition)}>
              {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Статус</label>
            <select value={pstatus} onChange={(e) => setPstatus(e.target.value as ProductStatus)}>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <button className="btn" onClick={save} disabled={busy || !categoryValue || !size || !price}>
          {busy ? "Сохраняем…" : "Сохранить"}
        </button>
      </div>
    </div>
  );
}

function ReservationConfirmSheet({ order, product, onClose }: { order: Order; product: Product; onClose: () => void }) {
  const nav = useNavigate();

  // Вместо сводки «забронировано» сразу выдаём сам талон: это тот документ, к
  // которому клиент вернётся в профиле и который покажет менеджеру.
  return (
    <Sheet open onClose={onClose}>
      <ReservationVoucher order={order} product={product} />
      <div style={{ height: 14 }} />
      <button className="btn" style={{ marginBottom: 10 }} onClick={() => nav("/profile")}>
        Талон в профиле
      </button>
      <button className="btn btn--secondary" onClick={onClose}>
        Остаться здесь
      </button>
    </Sheet>
  );
}

// Полноэкранный просмотр фото (зум по тапу). Тап по фону закрывает.
function ZoomOverlay({ productId, photoIdx, onClose }: { productId: number; photoIdx: number; onClose: () => void }) {
  const src = usePhotoSrc(productId, photoIdx, "md");
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(0,0,0,0.95)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {src && (
        <img
          src={src}
          alt=""
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      )}
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 16, right: 16,
          background: "rgba(255,255,255,0.15)", color: "#fff", border: "none",
          borderRadius: "50%", width: 40, height: 40, fontSize: 20, cursor: "pointer",
        }}
        aria-label="Закрыть"
      >✕</button>
    </div>
  );
}

/**
 * Главное действие карточки на системной кнопке Telegram.
 *
 * Отдельным компонентом, а не хуком в теле страницы: страница до загрузки
 * товара возвращает скелет, а хуки нельзя вызывать под условием. Размонтирование
 * (ушли с экрана, открыли шторку, товар забрали) само убирает кнопку.
 */
function BuyMainButton({ label, busy, onPress }: { label: string; busy: boolean; onPress: () => void }) {
  useMainButton({ text: label, onClick: onPress, progress: busy });
  return null;
}

export default function ProductPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, viewAs } = useUserStore();
  const { ids: likedIds, load: loadLikes, toggle: toggleLike } = useLikesStore();
  const [product, setProduct] = useState<Product | null>(null);
  const [busy, setBusy] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState<Order | null>(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [supportUrl, setSupportUrl] = useState<string | null>(null);
  const [payOrder, setPayOrder] = useState<Order | null>(null);   // демо-платёжка
  const [receipt, setReceipt] = useState<Receipt | null>(null);   // чек после оплаты
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [similar, setSimilar] = useState<Product[]>([]);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  useEffect(() => {
    getConfig().then((c) => { setBotUsername(c.bot_username); setPaymentsEnabled(c.payments_enabled); setSupportUrl(c.support_url); }).catch(() => {});
  }, []);
  useEffect(() => { loadLikes(); }, [loadLikes]);

  // Лайк со страницы товара: переключаем + оптимистично правим счётчик «в избранном».
  const onToggleLike = () => {
    if (!product) return;
    const wasLiked = likedIds.has(product.id);
    haptic.tap();
    toggleLike(product.id);
    setProduct({ ...product, likes: Math.max(0, (product.likes ?? 0) + (wasLiked ? -1 : 1)) });
  };

  useEffect(() => {
    if (!id) return;
    const pid = Number(id);
    getProduct(pid).then(setProduct);
    // Похожие товары — для блока «вам может понравиться».
    similarProducts(pid).then(setSimilar).catch(() => setSimilar([]));
    // Недавно просмотренные + засчитываем просмотр (раз за сессию приложения).
    addRecentlyViewed(pid);
    if (shouldCountView(pid)) trackView(pid).catch(() => {});
  }, [id]);

  // При открытии: грузим миниатюры всех кадров (дёшево — мгновенный плейсхолдер
  // при листании) и md первого кадра. Остальные md подгружаются по мере листания.
  useEffect(() => {
    if (!product) return;
    const total = 1 + (product.extra_file_ids?.length ?? 0);
    for (let i = 0; i < total; i++) prefetchPhoto(product.id, i, "thumb");
    prefetchPhoto(product.id, 0, "md");
  }, [product]);

  // Подгружаем md соседних кадров, чтобы листание было без ожидания.
  useEffect(() => {
    if (!product) return;
    const total = 1 + (product.extra_file_ids?.length ?? 0);
    if (photoIdx + 1 < total) prefetchPhoto(product.id, photoIdx + 1, "md");
    if (photoIdx - 1 >= 0) prefetchPhoto(product.id, photoIdx - 1, "md");
  }, [product, photoIdx]);

  useDocumentTitle(product ? (product.title ?? categoryLabel(product.category)) : null);

  if (!product) return <ProductSkeleton />;

  const effectiveRole = viewAs ?? user?.role;
  const canEdit = effectiveRole === "manager" || effectiveRole === "owner" || effectiveRole === "admin";
  const isLiked = likedIds.has(product.id);
  const inTg = isTelegram();
  // Системная кнопка рисуется клиентом поверх всего, включая наши шторки, —
  // пока открыта любая из них, главное действие экрана прячем.
  const overlayOpen = reserveOpen || editOpen || zoomOpen || !!payOrder || !!receipt || !!confirmedOrder;

  const totalPhotos = 1 + (product.extra_file_ids?.length ?? 0);
  const prevPhoto = () => setPhotoIdx((i) => Math.max(0, i - 1));
  const nextPhoto = () => setPhotoIdx((i) => Math.min(totalPhotos - 1, i + 1));

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setDragging(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(dy) > Math.abs(dx)) return; // вертикальный скролл — не перехватываем
    // Сопротивление на крайних кадрах.
    const atEdge = (photoIdx === 0 && dx > 0) || (photoIdx === totalPhotos - 1 && dx < 0);
    setDragX(atEdge ? dx * 0.3 : dx);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    // Касание началось на контроле (стрелка/точка) — touchStartX не выставлен: игнорируем.
    if (touchStartX.current === null) { setDragging(false); setDragX(0); return; }
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = touchStartY.current !== null ? e.changedTouches[0].clientY - touchStartY.current : 0;
    touchStartX.current = null;
    touchStartY.current = null;
    setDragging(false);
    setDragX(0);
    // Короткий тап без движения → открыть зум.
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) { setZoomOpen(true); return; }
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (dx < -40) nextPhoto();
    else if (dx > 40) prevPhoto();
  };

  const buy = async () => {
    setBusy(true);
    try {
      const order = await createOrder(product.id, "purchase");
      if (paymentsEnabled) {
        // Прод: настоящая оплата через Telegram Payments.
        const { link } = await createInvoiceLink(order.id);
        const tg = window.Telegram?.WebApp;
        if (tg?.openInvoice) {
          tg.openInvoice(link, async (s) => {
            if (s === "paid") {
              haptic.success();
              try { setReceipt(await getReceipt(order.id)); } catch { nav("/profile"); }
            }
          });
        } else {
          window.open(link, "_blank");
        }
      } else {
        // Демо: показываем платёжку-заглушку.
        setPayOrder(order);
      }
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };

  const reserve = async (rt: ReservationType) => {
    setBusy(true);
    try {
      const order = await createOrder(product.id, "reservation", rt);
      haptic.success();
      setReserveOpen(false);
      setConfirmedOrder(order);
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };

  const shareProduct = async () => {
    const caption = `${product.title ?? categoryLabel(product.category)} · ${product.size} · ${Math.round(product.price).toLocaleString("ru-RU")} ₽`;

    // В Telegram — нативный шеринг диплинка на Mini App.
    const tg = window.Telegram?.WebApp;
    if (isTelegram() && botUsername) {
      const link = `https://t.me/${botUsername}?startapp=product_${product.id}`;
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(caption)}`;
      if (tg?.openTelegramLink) tg.openTelegramLink(shareUrl);
      else window.open(shareUrl, "_blank");
      return;
    }

    // На сайте — ссылка на страницу товара (Web Share API, иначе копируем в буфер).
    const url = `${window.location.origin}/product/${product.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: caption, url }); return; } catch { /* отменили — не ошибка */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      notify("Ссылка на товар скопирована.");
    } catch {
      window.prompt("Ссылка на товар:", url);
    }
  };

  const ask = async () => {
    if (!supportUrl) return;
    const link = botUsername ? `https://t.me/${botUsername}?startapp=product_${product.id}` : "";
    const text =
      `Здравствуйте! Интересует товар #${product.sku} — ` +
      `${product.title ?? categoryLabel(product.category)}, ${Math.round(product.price).toLocaleString("ru-RU")} ₽` +
      (link ? `\n${link}` : "");
    const copied = await askAboutProduct(supportUrl, text);
    if (copied) notify("Вопрос о товаре скопирован — вставьте его в чат с менеджером.");
  };

  return (
    <div className="container">
      {/* В Telegram «назад» живёт системной стрелкой в шапке (useBackButton в
          App.tsx), поэтому своя кнопка здесь только для сайта. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {!isTelegram() ? (
          <button className="btn btn--secondary" onClick={() => nav(-1)} style={{ width: "auto" }}>
            ← Назад
          </button>
        ) : <span />}
        {canEdit && (
          <button
            className="btn btn--secondary"
            onClick={() => setEditOpen(true)}
            style={{ width: "auto" }}
          >
            ✏️ Изменить
          </button>
        )}
      </div>

      {/* Карусель фотографий со слайд-анимацией */}
      <div
        style={{ margin: "12px 0", position: "relative", userSelect: "none" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div style={{ overflow: "hidden", borderRadius: 16 }}>
          <div style={{
            display: "flex",
            transform: `translateX(calc(${-photoIdx * 100}% + ${dragX}px))`,
            transition: dragging ? "none" : "transform 0.28s ease",
          }}>
            {Array.from({ length: totalPhotos }).map((_, i) => (
              <div key={i} style={{ flex: "0 0 100%", width: "100%" }}>
                <ProductPhoto productId={product.id} photoIdx={i} alt={product.title ?? product.sku} rounded variant="md" lowVariant="thumb" />
              </div>
            ))}
          </div>
        </div>

        {/* Плавающие действия поверх фото: лайк и шеринг. */}
        <div
          style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 8, zIndex: 3 }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <button
            onClick={onToggleLike}
            aria-label="В избранное"
            style={{
              background: "rgba(0,0,0,0.45)", border: "none", borderRadius: "50%",
              width: 38, height: 38, fontSize: 18, cursor: "pointer", lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: isLiked ? "#ff5a5f" : "rgba(255,255,255,0.95)",
              backdropFilter: "blur(2px)",
            }}
          >{isLiked ? "♥" : "♡"}</button>
          <button
            onClick={shareProduct}
            aria-label="Поделиться"
            style={{
              background: "rgba(0,0,0,0.45)", border: "none", borderRadius: "50%",
              width: 38, height: 38, fontSize: 16, cursor: "pointer", lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.95)", backdropFilter: "blur(2px)",
            }}
          >↗</button>
        </div>

        {totalPhotos > 1 && (
          <>
            {photoIdx > 0 && (
              <button
                onClick={prevPhoto}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                style={{
                  position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                  background: "rgba(0,0,0,0.4)", color: "#fff", border: "none",
                  borderRadius: "50%", width: 36, height: 36, fontSize: 18, cursor: "pointer",
                }}
              >‹</button>
            )}
            {photoIdx < totalPhotos - 1 && (
              <button
                onClick={nextPhoto}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "rgba(0,0,0,0.4)", color: "#fff", border: "none",
                  borderRadius: "50%", width: 36, height: 36, fontSize: 18, cursor: "pointer",
                }}
              >›</button>
            )}
            <div
              style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 8 }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              {Array.from({ length: totalPhotos }).map((_, i) => (
                <span
                  key={i}
                  onClick={() => setPhotoIdx(i)}
                  style={{
                    width: 8, height: 8, borderRadius: "50%", cursor: "pointer",
                    background: i === photoIdx ? "var(--accent)" : "var(--border)",
                  }}
                />
              ))}
            </div>
          </>
        )}

        {/* Тот же штамп, что в каталоге, и внутри галереи — чтобы лечь по
            центру фотографии. Кнопки покупки у такого товара нет, поэтому
            жестов на экране по-прежнему два. */}
        {product.status !== "available" && (
          <HandSeal
            variant={product.status === "sold" ? 1 : 2}
            className={`product__seal product__seal--${product.status === "sold" ? "sold" : "hold"}`}
          />
        )}
      </div>

      {zoomOpen && (
        <ZoomOverlay productId={product.id} photoIdx={photoIdx} onClose={() => setZoomOpen(false)} />
      )}

      {/* Название товара — главный заголовок страницы: и семантически (h1 на
          экране один), и по типографике — на нём стоит дисплейный шрифт. */}
      <h1 style={{ margin: "0 0 8px" }}>{product.title ?? categoryLabel(product.category)}</h1>
      <p className="micro" style={{ margin: "6px 0" }}>📏 {product.size}</p>
      <p style={{ margin: "4px 0 14px", fontWeight: 700, fontSize: 20, color: "var(--accent)" }}>
        <span className="hand-wrap">
          {Math.round(product.price).toLocaleString("ru-RU")} ₽
          <HandUnderline className="hand-underline" />
        </span>
      </p>
      <p className="micro" style={{ margin: "6px 0", color: "var(--hint)" }}>#{product.sku}</p>
      {((product.likes ?? 0) > 0 || (product.views ?? 0) > 0) && (
        <p className="micro" style={{ margin: "6px 0", color: "var(--hint)", display: "flex", gap: 14 }}>
          {(product.likes ?? 0) > 0 && <span>❤️ {product.likes} в избранном</span>}
          {(product.views ?? 0) > 0 && <span>👁 {product.views} просмотров</span>}
        </p>
      )}
      <p style={{ margin: "8px 0" }}>
        <span className={`badge badge--${product.status === "available" ? "avail" : product.status === "on_hold" ? "hold" : "sold"}`}>
          {product.status === "available" ? "В наличии" : product.status === "on_hold" ? "В броне" : "Куплено"}
        </span>
      </p>

      {/* Состояние вещи. Раньше поле заполнял менеджер, а видел его только он
          же: для винтажа это вторая характеристика после размера, и без неё
          решение о покупке принимают вслепую. */}
      <div className="condition">
        <span className="micro" style={{ color: "var(--hint)" }}>Состояние</span>
        <div style={{ textAlign: "right", minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {CONDITION_LABELS[product.condition].label}
          </span>
          {/* Расшифровка — обычный текст, а не микро-капс: это фраза, а не
              подпись, и в капсе с широким трекингом она перестаёт читаться. */}
          <p style={{ margin: "2px 0 0", color: "var(--hint)", fontSize: 13, lineHeight: 1.35 }}>
            {CONDITION_LABELS[product.condition].hint}
          </p>
        </div>
      </div>

      {supportUrl && (
        <>
          <button className="btn btn--secondary" onClick={ask}>💬 Спросить о товаре</button>
          <div style={{ height: 8 }} />
        </>
      )}

      {product.status === "available" && (
        <>
          {/* В Telegram «Купить» уезжает на системную кнопку снизу — её в
              мини-аппе и ищут внизу экрана. На сайте MainButton нет, поэтому
              там остаётся своя кнопка с рукой. */}
          {inTg ? (
            !overlayOpen && (
              <BuyMainButton
                label={`Купить · ${Math.round(product.price).toLocaleString("ru-RU")} ₽`}
                busy={busy}
                onPress={() => { haptic.press(); buy(); }}
              />
            )
          ) : (
            <>
              <button className="btn btn--tilt btn--hand" onClick={buy} disabled={busy}>
                Купить
                <HandArrow className="hand-arrow" />
              </button>
              <div style={{ height: 8 }} />
            </>
          )}
          <button
            className="btn btn--secondary"
            onClick={() => { haptic.tap(); setReserveOpen(true); }}
            disabled={busy}
          >
            Забронировать
          </button>
        </>
      )}

      {/* Условия брони — шторкой: три варианта с залогами читаются как выбор,
          а не как ещё три кнопки, дописанные под первой. */}
      <Sheet open={reserveOpen} title="Забронировать" onClose={() => setReserveOpen(false)}>
        <button className="btn btn--secondary" disabled={busy} onClick={() => { haptic.press(); reserve("free"); }}>
          Бесплатно — 24ч
        </button>
        <div style={{ height: 8 }} />
        <button className="btn btn--secondary" disabled={busy} onClick={() => { haptic.press(); reserve("week"); }}>
          Неделя — залог 5% ({Math.round(product.price * 0.05).toLocaleString("ru-RU")} ₽)
        </button>
        <div style={{ height: 8 }} />
        <button className="btn btn--secondary" disabled={busy} onClick={() => { haptic.press(); reserve("month"); }}>
          Месяц — залог 10% ({Math.round(product.price * 0.10).toLocaleString("ru-RU")} ₽)
        </button>
        <p className="micro" style={{ color: "var(--hint)", marginTop: 14 }}>
          Залог засчитывается в стоимость покупки.
        </p>
      </Sheet>

      {editOpen && (
        <EditSheet
          product={product}
          onSave={(updated) => { setProduct(updated); setEditOpen(false); }}
          onClose={() => setEditOpen(false)}
        />
      )}

      {confirmedOrder && (
        <ReservationConfirmSheet
          order={confirmedOrder}
          product={product}
          onClose={() => setConfirmedOrder(null)}
        />
      )}

      {/* Демо-платёжка после «Купить» (когда онлайн-оплата не настроена). */}
      {payOrder && (
        <PaymentSheet
          order={payOrder}
          product={product}
          onClose={() => setPayOrder(null)}
          onPaid={(r) => { setPayOrder(null); setReceipt(r); }}
        />
      )}

      {/* Чек после успешной оплаты. */}
      {receipt && (
        <ReceiptSheet receipt={receipt} onClose={() => { setReceipt(null); nav("/profile"); }} />
      )}

      {/* Похожие товары — спасает «тупик», углубляет сессию. */}
      <ProductScroller title="Вам может понравиться" products={similar} />
    </div>
  );
}
